import { motion } from "framer-motion";
import { ShoppingBag, ArrowUpRight, CreditCard, Building2 } from "lucide-react";
import BooksSection from "@/components/BooksSection";
import ModulesBento from "@/components/ModulesBento";
import PricingBlock from "@/components/PricingBlock";
import { FAQSection } from "@/components/FAQSection";
import EditorialMarquee from "@/components/EditorialMarquee";
import { startStripeCheckout } from "@/lib/payments";
import { useAuth } from "@/context/AuthContext";
import { useTrackView } from "@/lib/trackInterest";

const EASE = [0.65, 0, 0.35, 1];
const VIEWPORT = { once: true, margin: "-80px" };
const HERO_MOTION = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, ease: EASE } };

const B2B_TIERS = [
  { key: "b2b_starter", name: "Corporate — Starter", price: "$1,200", note: "per quarter · up to 25", features: ["4 core modules", "1 Kindle site license", "Slack/Teams onboarding kit"] },
  { key: "b2b_team", name: "Corporate — Team", price: "$4,800", note: "per quarter · 25–250", features: ["All 8 modules + bulk paperbacks", "Live pre-shift workshops", "Manager training + dashboard"], featured: true },
];

export default function ShopPage() {
  const { user } = useAuth();
  const userId = user && user.id ? user.id : null;
  useTrackView("shop", "Shop · all products");

  return (
    <main className="pt-32 md:pt-40" data-testid="shop-page">
      {/* Hero */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pb-16">
        <motion.div {...HERO_MOTION} className="grid grid-cols-12 gap-y-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <ShoppingBag size={16} className="text-[#B45309]" strokeWidth={1.6} />
              <span className="section-tag !mb-0">§ Shop · Everything T.A.C.C.</span>
            </div>
            <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.88] tracking-tighter">
              The <span className="italic text-[#B45309]">store</span>.
            </h1>
          </div>
          <div className="col-span-12 md:col-span-4 flex items-end">
            <p className="text-[#78716C] text-lg leading-relaxed">
              Books, modules, the complete bundle, and corporate licenses — one page, straight to checkout. Card or external — your choice.
            </p>
          </div>
        </motion.div>
      </section>

      <EditorialMarquee />

      {/* All 8 modules with snippets + Preview */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-24 pb-4">
        <div className="section-tag mb-6">§ The 8 Modules · inside the Bundle</div>
        <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-3xl">
          Every module. Every <span className="italic text-[#B45309]">excerpt</span>.
        </h2>
      </section>
      <ModulesBento heading={false} />

      {/* 3-tier pricing (Workbook / Course / Bundle) */}
      <PricingBlock />
      <FAQSection />

      {/* Books */}
      <BooksSection />

      {/* B2B tiers direct checkout */}
      <section className="relative bg-[#FAF7F2] py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-y-10 mb-14">
            <div className="col-span-12 md:col-span-7">
              <div className="section-tag mb-6">§ For Companies · direct checkout</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
                License T.A.C.C. for your <span className="italic text-[#B45309]">workforce</span>.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
              <p className="text-[#78716C] leading-relaxed">
                Pay now for a quarter, or request a tailored proposal on the For Companies page.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-12 border border-[#E7DFD5]">
            {B2B_TIERS.map((t, i) => (
              <motion.div
                key={t.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={VIEWPORT}
                transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }}
                data-testid={`shop-b2b-${t.key}`}
                className={`col-span-12 md:col-span-6 p-8 md:p-12 flex flex-col ${i === 0 ? "md:border-r border-[#E7DFD5]" : "border-t md:border-t-0 border-[#E7DFD5]"} ${t.featured ? "bg-[#F5EFEB]" : ""}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <Building2 size={16} className="text-[#B45309]" strokeWidth={1.6} />
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C]">{t.note}</span>
                </div>
                <div className="font-display text-3xl md:text-4xl mb-3">{t.name}</div>
                <div className="font-display text-6xl md:text-7xl text-[#B45309] mb-8">{t.price}</div>
                <ul className="space-y-3 mb-10 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="text-sm text-[#44403C] flex items-start gap-3">
                      <span className="text-[#B45309] mt-1">✦</span> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => startStripeCheckout(t.key, userId)}
                  data-testid={`shop-buy-${t.key}`}
                  className={`${t.featured ? "btn-ember" : "btn-outline"} text-sm w-full justify-center`}
                >
                  <CreditCard size={16} /> Pay now — {t.price}
                  <ArrowUpRight size={16} />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
